/**
 * Cache Manager for Static Data
 * Reduces bandwidth by caching athletes, judges, and theme data
 * Cache expires after 5 minutes to ensure fresh data
 */

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const CACHE_VERSION = '1.0'; // Increment to invalidate all caches

const CACHE_KEYS = {
  ATHLETES: 'wffa_cache_athletes',
  JUDGES: 'wffa_cache_judges',
  THEME: 'wffa_cache_theme',
  TOURNAMENTS: 'wffa_cache_tournaments',
  VERSION: 'wffa_cache_version'
};

// Check if cache version matches, clear if not
const checkCacheVersion = () => {
  const storedVersion = localStorage.getItem(CACHE_KEYS.VERSION);
  if (storedVersion !== CACHE_VERSION) {
    clearAllCache();
    localStorage.setItem(CACHE_KEYS.VERSION, CACHE_VERSION);
  }
};

// Get cached data if valid
export const getCached = (key) => {
  try {
    checkCacheVersion();
    const cached = localStorage.getItem(CACHE_KEYS[key]);
    if (!cached) return null;
    
    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();
    
    // Check if cache is still valid
    if (now - timestamp < CACHE_DURATION) {
      return data;
    }
    
    // Cache expired, remove it
    localStorage.removeItem(CACHE_KEYS[key]);
    return null;
  } catch (error) {
    console.error('Cache read error:', error);
    return null;
  }
};

// Set cached data with timestamp
export const setCached = (key, data) => {
  try {
    const cacheItem = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEYS[key], JSON.stringify(cacheItem));
  } catch (error) {
    console.error('Cache write error:', error);
    // If localStorage is full, clear old caches
    if (error.name === 'QuotaExceededError') {
      clearAllCache();
      // Try again
      try {
        const cacheItem = {
          data,
          timestamp: Date.now()
        };
        localStorage.setItem(CACHE_KEYS[key], JSON.stringify(cacheItem));
      } catch (retryError) {
        console.error('Cache write failed after clearing:', retryError);
      }
    }
  }
};

// Invalidate specific cache
export const invalidateCache = (key) => {
  try {
    localStorage.removeItem(CACHE_KEYS[key]);
  } catch (error) {
    console.error('Cache invalidation error:', error);
  }
};

// Clear all caches
export const clearAllCache = () => {
  try {
    Object.values(CACHE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    console.error('Cache clear error:', error);
  }
};

// Wrapper function for API calls with cache
export const cachedFetch = async (key, fetchFunction) => {
  // Try to get from cache first
  const cached = getCached(key);
  if (cached) {
    return cached;
  }
  
  // If not in cache, fetch from API
  const data = await fetchFunction();
  
  // Store in cache
  setCached(key, data);
  
  return data;
};
